import { useState, useEffect, useCallback } from 'react';
import { productsAPI } from '@/lib/api';

// Validates cart items against live products. A product that was deleted (404)
// or hidden (is_active === false) after being added to the cart would otherwise
// hard-fail order creation. Returns the product_ids that are no longer available
// plus a revalidate() to re-check on demand (e.g. after a server rejection).
export function useUnavailableItems(items) {
  const [unavailableIds, setUnavailableIds] = useState([]);
  const cartIdsKey = items.map(i => i.product_id).join(',');

  const check = useCallback((setter) => {
    const ids = cartIdsKey ? cartIdsKey.split(',') : [];
    if (ids.length === 0) { setter([]); return; }
    // Single batch call: any id missing (deleted) or is_active=false (hidden)
    // is treated as unavailable.
    productsAPI.lookup(ids)
      .then(res => {
        const active = new Map((res?.data?.products || []).map(p => [p.id, p.is_active !== false]));
        setter(ids.filter(id => active.get(id) !== true));
      })
      .catch(() => setter([])); // transient error: don't false-flag; backend guard is authoritative
  }, [cartIdsKey]);

  useEffect(() => {
    let active = true;
    check(res => { if (active) setUnavailableIds(res); });
    return () => { active = false; };
  }, [check]);

  const revalidate = useCallback(() => check(setUnavailableIds), [check]);

  return { unavailableIds, revalidate };
}
